/** @file Top bar shared by the shell screens: app name, page title, Projects / Settings / Sign out. */
import type { Translate } from '@pose2d/i18n/types';
import { useState, type ReactNode } from 'react';
import { api } from '../api/api';
import { errorText } from './i18n/errorText';
import { navigate } from './router/navigate';
import { signOut } from './session/signOut';

/**
 * `title` is display text already (a translated page name or a project name); a failed sign-out shows as an alert.
 * `actions` (e.g. the editor's Export button) sit before the Projects button.
 */
export function ShellHeader({ t, title, actions }: { t: Translate; title: string; actions?: ReactNode }) {
  const [failure, setFailure] = useState<{ thrown: unknown } | null>(null);
  const onSignOut = (): void => {
    setFailure(null);
    signOut(api).catch((thrown: unknown) => { setFailure({ thrown }); });
  };
  return (
    <header className="shell-header">
      <strong className="shell-brand">{t('app.title')}</strong>
      <span className="shell-title">{title}</span>
      <nav className="shell-nav">
        {actions}
        <button type="button" onClick={() => { navigate('/'); }}>{t('shell.projects')}</button>
        <button type="button" onClick={() => { navigate('/settings'); }}>{t('shell.settings')}</button>
        <button type="button" onClick={onSignOut}>{t('shell.signOut')}</button>
      </nav>
      {failure === null ? null : <p role="alert" className="shell-error">{errorText(t, failure.thrown)}</p>}
    </header>
  );
}
